import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@core/auth/hooks/useAuth';
import { getModuleByPath } from './moduleRegistry';
import { useAccessibleModules } from './useAccessibleModules';

/** Page shown after login when the user has several work modules and no remembered one */
export const MODULE_CHOOSER_PATH = '/izbor-modula';

// Per-user key so a shared computer does not mix users. Only the module id is stored;
// access is always re-checked against permissions before it is used.
const LAST_MODULE_KEY_PREFIX = 'erp.lastModule.';

export const getLastModuleId = (userId: string): string | null => {
  try {
    return localStorage.getItem(LAST_MODULE_KEY_PREFIX + userId);
  } catch {
    return null;
  }
};

const setLastModuleId = (userId: string, moduleId: string) => {
  try {
    localStorage.setItem(LAST_MODULE_KEY_PREFIX + userId, moduleId);
  } catch {
    // Storage unavailable (private mode, blocked site data) - landing falls back to the defaults
  }
};

/**
 * Remembers the work module the user is in, so the next login lands there.
 * Administrative modules (e.g. Users) are never remembered.
 */
export const useRememberLastModule = () => {
  const { user } = useAuth();
  const { accessibleModules } = useAccessibleModules();
  const location = useLocation();

  useEffect(() => {
    if (!user?.id) return;

    const module = getModuleByPath(location.pathname)?.moduleInfo;
    if (!module || module.isAdministrative) return;
    if (!accessibleModules.some((m) => m.id === module.id)) return;

    setLastModuleId(user.id, module.id);
  }, [user?.id, location.pathname, accessibleModules]);
};
